import {
  IonDatetime,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';

import { checkmark } from 'ionicons/icons';

import { useEnter } from '../../../hooks/Utils/useEnter';
import { PetForm_1 as PetFormType_1, PetForm_1_Defaults } from '../../../types/Pet/types';
import { capitalizeEveryWord } from '../../../utils/string';
import { validations } from '../../../utils/validations';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import Modal from '../../UI/Modal';
import Spinner from '../../UI/Spinner';
import './PetForm_1.scss';
import { PetForm_1Props } from './types';

type FieldType = 'text' | 'select' | 'modal' | 'date';
interface FieldConfig {
  name: string;
  label: string;
  type: FieldType;
  options?: { label: string; value: string }[];
  transform?: (v: string) => string;
  rules?: Record<string, unknown>;
}

const FIELDS: FieldConfig[] = [
  {
    name: 'nombre',
    label: 'Nombre',
    type: 'text',
    rules: {
      required: 'Por favor, ingrese el nombre de la mascota',
      pattern: validations.name,
    },
    transform: capitalizeEveryWord,
  },
  {
    name: 'animal',
    label: 'Animal',
    type: 'select',
    options: [
      { label: 'Perro', value: 'perro' },
      { label: 'Gato', value: 'gato' },
    ],
    rules: { required: 'Por favor, seleccione el animal' },
  },
  {
    name: 'raza',
    label: 'Raza',
    type: 'modal',
    rules: { required: 'Por favor, seleccione la raza' },
  },
  {
    name: 'sexo',
    label: 'Sexo',
    type: 'select',
    options: [
      { label: 'Macho', value: 'M' },
      { label: 'Hembra', value: 'H' },
    ],
    rules: { required: 'Por favor, seleccione el sexo' },
  },
  { name: 'color', label: 'Color', type: 'text', transform: capitalizeEveryWord, rules: { required: 'Por favor, ingrese el color' } },
  {
    name: 'fecha_nacimiento',
    label: 'Fecha de nacimiento',
    type: 'date',
    rules: {
      required: 'Por favor, ingrese la fecha de nacimiento',
      validate: validations.validatePetBirthDate,
    },
  },
];

const PetForm_1: React.FC<PetForm_1Props> = ({
  pet,
  setPet,
  petData,
  razas,
  goForward,
  isSubmitting,
  isLoading = false,
}) => {
  const { ref, handleEnter } = useEnter();
  const prevAnimal = useRef<string | null>(null);

  const normalizedPet = useMemo(
    () => (pet ? { ...pet, animal: pet.animal?.toLowerCase() } : PetForm_1_Defaults),
    [pet],
  );

  const { control, handleSubmit, watch, setValue, errors } = useForm<PetFormType_1>({
    defaultValues: normalizedPet,
  });

  const [animal, selectedRaza] = [watch('animal'), watch('raza')];
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(pet?.raza || '');

  const options = useMemo(() => {
    const opts = animal === 'perro' ? razas.perro : animal === 'gato' ? razas.gato : [];
    return [...(opts || [])].sort((a, b) => a.label.localeCompare(b.label));
  }, [animal, razas]);

  const filteredOptions = useMemo(() => {
    const s = search.toLowerCase().trim();
    return s ? options.filter((r) => r.label.toLowerCase().includes(s)) : options;
  }, [options, search]);

  const onSubmit = async (data: PetFormType_1) => {
    if (!petData) setPet(data);
    await goForward(data);
  };

  const handleSelectRaza = (r: { value: string; label: string }) => {
    setValue('raza', r.value, { shouldValidate: true });
    setIsOpen(false);
  };

  // Reset raza cuando cambia animal
  useEffect(() => {
    if (prevAnimal.current && prevAnimal.current !== animal) {
      setValue('raza', '');
      setSearch('');
    }
    prevAnimal.current = animal;
  }, [animal, setValue]);

  const renderField = (field: FieldConfig, value: any, onChange: (v: any) => void) => {
    const { type, label, options: opts, transform } = field;

    const wrapItem = (children: React.ReactNode, floating = false) => (
      <IonItem {...(type === 'modal' ? { onClick: () => setIsOpen(true) } : {})}>
        <IonLabel position={floating ? 'floating' : undefined}>{label}</IonLabel>
        {children}
      </IonItem>
    );

    switch (type) {
      case 'text':
        return wrapItem(
          <IonInput
            value={value ?? ''}
            onIonChange={(e) => onChange(e.detail.value ?? '')}
            onIonBlur={() => transform && value && onChange(transform(value))}
          />,
          true,
        );

      case 'select':
        return wrapItem(
          <IonSelect
            value={value}
            placeholder="Seleccione"
            onIonChange={(e) => onChange(e.detail.value!)}
            okText="Aceptar"
            cancelText="Cancelar"
          >
            {opts!.map((o) => (
              <IonSelectOption key={o.value} value={o.value}>
                {o.label}
              </IonSelectOption>
            ))}
          </IonSelect>,
        );

      case 'modal':
        return wrapItem(
          <IonInput
            readonly
            value={options.find((r) => r.value === selectedRaza)?.label || ''}
          />,
          true,
        );

      case 'date':
        return wrapItem(
          <IonDatetime
            value={value}
            onIonChange={(e) => onChange(e.detail.value!)}
            displayFormat="DD/MM/YYYY"
            doneText="Aceptar"
            cancelText="Cancelar"
          />,
          true,
        );

      default:
        return null;
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
        {FIELDS.map((field) => (
          <FormItem
            key={field.name}
            control={control}
            name={field.name}
            rules={field.rules}
            errors={errors}
            loading={isLoading}
            render={({ value, onChange }) => renderField(field, value, onChange)}
          />
        ))}

        <Button ref={ref} type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <Spinner color="light" />
          ) : petData ? (
            'Enviar solicitud de cambio'
          ) : (
            'Siguiente'
          )}
        </Button>
      </form>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className="raza-modal">
          <IonSearchbar
            value={search}
            debounce={300}
            onIonChange={(e: CustomEvent) => setSearch(e.detail.value || '')}
            placeholder="Buscar raza..."
          />

          {!filteredOptions.length ? (
            <div className="ion-padding">No se encontraron razas</div>
          ) : (
            <Virtuoso
              className="virtuoso-container"
              totalCount={filteredOptions.length}
              itemContent={(i) => {
                const r = filteredOptions[i];
                return (
                  <IonItem key={r.value} button onClick={() => handleSelectRaza(r)}>
                    <IonLabel>{r.label}</IonLabel>
                    {selectedRaza === r.value && (
                      <IonIcon icon={checkmark} slot="end" color="success" />
                    )}
                  </IonItem>
                );
              }}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default React.memo(PetForm_1);
